import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import css from '@emotion/css';
import { breakpoints } from 'app/spacing';
import { monoFontFamily } from 'app/typography';
import { markdownPreviewStyles } from '__components/WYSIWYG/MarkdownEditor/styles';
import { shadowLight } from 'app/colors';

const getWrapperStyles = (fullScreen: boolean, inModal = false) => {
  if (inModal) {
    return '';
  }

  if (fullScreen) {
    return `
      height: 100vh;
      padding-bottom: 20px;
    `;
  }
  return `
      border-radius: 16px;
        ${shadowLight};
      padding-bottom: 20px;
    `;
};

export const ChatWrapper = styled.div<{
  fullScreen: boolean;
  inModal?: boolean;
}>`
  width: 100%;
  background-color: var(--color-background-white);
  ${({ fullScreen, inModal }) => getWrapperStyles(fullScreen, inModal)};
  position: relative;
`;

function getPaddingBottom(
  isMobile: boolean,
  isAutoResponsesDisplayed: boolean
) {
  if (isMobile && isAutoResponsesDisplayed) {
    return '250px';
  } else if (isAutoResponsesDisplayed) {
    return '100px';
  } else {
    return '20px';
  }
}

export const MessageHistoryWrapper = styled.div<{
  loading: boolean;
  isAutoResponsesDisplayed: boolean;
  isMobile: boolean;
  fullHeight: boolean;
  sidePadding: string;
  topPadding: string;
  shrinkHeightAmount?: number;
}>`
  margin-top: 1px;
  width: 100%;

  height: ${({ fullHeight, shrinkHeightAmount = 0 }) =>
    fullHeight
      ? `calc(100vh - ${131 + shrinkHeightAmount}px)`
      : `calc(100vh - ${428 + shrinkHeightAmount}px)`};
  overflow-x: scroll;
  display: flex;
  flex-direction: column;
  gap: 25px;
  ${({ sidePadding }) => sidePadding};
  ${({ fullHeight }) =>
    fullHeight
      ? ''
      : css`
          min-height: 239px;
        `} // increases total chat height to 400px
  padding-bottom: ${({ isMobile, isAutoResponsesDisplayed }) =>
    getPaddingBottom(isMobile, isAutoResponsesDisplayed)};
  padding-top: ${({ topPadding, fullHeight }) =>
    topPadding ? topPadding : fullHeight ? '0' : '25'}px;
  @media screen and (min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.lg +
    50}px) {
    padding-bottom: 140px;
  }
  justify-content: ${({ loading }) => (loading ? 'center' : 'flex-start')};
`;

export const MessageWrapper = styled.div<{ alignment: string }>`
  display: flex;
  ${({ alignment }) => css`
    justify-content: ${alignment === 'right' ? 'flex-end' : 'flex-start'};
  `}
`;

export const GradientTopWrapper = styled.div<{
  borderRadius: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 10px;
  border-radius: ${({ borderRadius }) => borderRadius};
`;

export const GradientBottomWrapper = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 10px;
`;

export const Message = styled.div<{
  isSender: boolean;
  color: string;
}>`
  ${({ color }) => css`
    background-color: ${color};
  `}
  color: var(--color-background-white);
  border-radius: 10px;
  padding: 10px;
  max-width: 65%;
  white-space: pre-wrap;
  border: 1px solid
    ${({ isSender }) =>
      isSender
        ? 'var(--color-viz-cat-2-secondary)'
        : 'var(--color-feedback-success)'};
`;

export const MessageTop = styled.div`
  width: 100%;
  padding-bottom: 10px;
  padding-top: 5px;
  padding-left: 5px;
`;

export const MessageBottom = styled.div`
  padding: 0 10px 15px 10px;
`;

export const MesssageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 10px;
  color: var(--color-font-primary);
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  padding-top: 10px;
`;

export const FloatingResponsesWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Responses = styled.div<{
  isMobile: boolean;
  isOdd: boolean;
  sidePadding: string;
}>`
  display: flex;
  grid-template-columns: ${({ isOdd }) => (isOdd ? '1fr 1fr' : '1fr')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};

  justify-content: center;
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '5px' : '10px')};
  position: absolute;
  transform: translateX(-50%);
  width: 100%;
  max-height: 60px;

  left: 50%;
  @media screen and (min-width: ${breakpoints.lg}px) and (max-width: ${breakpoints.lg +
    50}px) {
    top: ${({ isMobile }) => (isMobile ? '-120px' : '-100px')};
  }
  animation: fadeIn ease 0.5s;
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  button {
    background-color: var(--color-background-white);
    border: 1px solid var(--color-button-secondary-default);
    border-radius: 8px;
    color: var(--color-button-secondary-default);
    text-align: center;
    text-decoration: none;
    display: inline-block;
    font-size: 16px;
    margin: 4px 2px;
    padding: 13px 15px 13px 15px;
    cursor: pointer;
    box-shadow: 0 18px 26px
      color-mix(in srgb, var(--color-background-black) 3%, transparent);
  }
  button:active {
    outline: none;
  }
  button:focus {
    outline: none;
  }
  button:hover {
    background-color: var(--color-button-secondary-default);
    color: var(--color-background-white);
  }
  button:active {
    background-color: var(--color-button-secondary-hover);
    color: var(--color-background-white);
  }
  button:disabled {
    border: 1px solid var(--color-button-tertiary-default);
    color: var(--color-button-tertiary-default);
    background-color: var(--color-background-white);
    cursor: auto;
  }
`;

export const MessageInput = styled.div<{ chatPadding: string }>`
  width: 100%;
  ${({ chatPadding }) => chatPadding}
`;

export const ResponseLoadingWrapper = styled.span<{ inline?: boolean }>`
  display: inline;
  ${({ inline }) =>
    inline
      ? ''
      : css`
          width: 100%;
        `}
`;

export const InlineTypographyWrapper = styled.span`
  display: inline;
`;

export const LoadingDotsWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  padding-bottom: 1px;
  margin-left: 2px;
`;

const jump = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-2px); }
`;

export const Dot = styled.div<{ color: string; size?: string }>`
  background-color: var(--color-${({ color }) => color});
  border-radius: 50%;
  width: ${({ size }) => size || '2px'};
  height: ${({ size }) => size || '2px'};
  margin: 0 2px;
  animation: ${jump} 1s infinite both;
  will-change: transform;
`;

export const MarkdownContainer = styled.div<{ color?: string }>`
  ${({ color }) =>
    markdownPreviewStyles({
      color: color ? `var(--color-${color})` : undefined,
    })};
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.016em;
  white-space: normal !important;

  & *,
  & *::before,
  & *::after {
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    white-space: inherit;
  }

  a {
    color: var(--color-);
    text-decoration: underline;
    line-height: 1.5;
  }

  p {
    margin: 0.5em 0;
    line-height: 1.5;
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  /* Sized headings win over the & * font-size reset above because the
     element selectors are more specific than the universal selector */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0.75em;
  }

  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child,
  h5:first-child,
  h6:first-child {
    margin-top: 0;
  }

  h1 {
    font-size: 1.3em;
  }

  h2 {
    font-size: 1.2em;
  }

  h3 {
    font-size: 1.1em;
  }

  h4 {
    font-size: 1.05em;
  }

  h5 {
    font-size: 1em;
  }

  h6 {
    font-size: 0.9em;
  }

  /* Lists */
  ul {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    margin-left: 1em;
    padding-left: 10px;
  }

  ol {
    margin-top: 1em;
    margin-bottom: 1em;
    margin-left: 1em;
    padding-left: 10px;
    line-height: 1.5;
  }
  li {
    margin: 0.15em 0 0.15em 0;
    line-height: 1.5;
  }

  li p {
    margin: 0;
    display: inline; /* or block if you want new lines, but no extra margin */
    line-height: 1.5;
  }

  code {
    font-family: ${monoFontFamily};
    line-height: 1.5;
    color: var(--color-ai-user-bg);
    background: var(--color-ai-system-bg);
    border-radius: 4px;
    padding: 1px 4px;
    font-size: 0.9em;
  }

  pre {
    border: 1px solid var(--color-ai-user-bg);
    border-radius: 8px;
    padding: 16px;
    margin: 0.75em 0;
    overflow: auto;
    max-width: 100%;

    /* Firefox only — Chrome ignores ::-webkit-scrollbar styling when the
       standard scrollbar properties are set (same guard as kds base.css) */
    @supports (-moz-appearance: none) {
      scrollbar-width: thin;
      scrollbar-color: var(--color-ai-user-bg) transparent;
    }
  }

  pre::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background: transparent;
  }

  pre::-webkit-scrollbar-thumb {
    background: var(--color-ai-user-bg);
    border-radius: 4px;
  }

  pre code {
    background: none;
    color: inherit;
    border-radius: 0;
    padding: 0;
    white-space: pre;
    font-size: 12px;
    display: block;
  }
  img {
    max-width: 100%;
    height: auto;
  }

  /* Tables */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.75em 0;
    font-size: 13px;
    table-layout: fixed;
  }

  thead {
    background-color: inherit;
  }

  th {
    font-weight: 600;
    text-align: left;
    padding: 8px 12px;
    border: 1px solid currentColor;
    opacity: 0.9;
    word-break: break-word;
  }

  td {
    padding: 7px 12px;
    border: 1px solid currentColor;
    opacity: 0.85;
    vertical-align: top;
    word-break: break-word;
  }

  tr:nth-child(even) {
    background-color: inherit;
  }
`;

export const ControlWrapper = styled.div<{
  chatPadding: string;
}>`
  ${({ chatPadding }) => chatPadding};
  display: flex;
  justify-content: end;
  padding-top: 10px;
  gap: 5px 15px;
  align-items: center;
  min-width: 250px;
`;

export const ScrollToBottom = styled.div<{ higher: boolean }>`
  position: absolute;
  bottom: ${({ higher }) => (higher ? '180px' : '150px')};
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const Error = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 10%;
  display: flex;
  padding-top: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;
